/**
 * DirectoryBrowserTab — paginated browser for users / groups / devices
 * synced from any of the tenant's directory integrations.
 *
 * Layout uses `SplitPage` so the per-entity detail opens in a
 * resizable right drawer (collapsable via the chevron, same chrome
 * the trace inspector uses). The drawer hosts its own `TabStrip`:
 *   - User detail   → Overview, Groups
 *   - Group detail  → Overview, Members, Sub-groups
 *   - Device detail → Overview
 *
 * Cross-entity navigation (clicking a group from inside a user
 * panel, or a user from inside a group's member list) switches the
 * active section *and* re-targets the drawer at the new entity.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    listDirectoryUsers,
    listDirectoryGroups,
    listDirectoryDevices,
    listDirectoryIntegrations,
    getDirectoryUser,
    getDirectoryGroup,
    getDirectoryDevice,
} from "../../lib/identityClient.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import DataTable from "../DataTable.jsx";
import SplitPage from "../SplitPage.jsx";
import TabStrip from "../TabStrip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { fullTimestampFormatter, timeLabelFormatter, dayLabelFormatter, parseServerTimestamp } from "../../lib/traceUtils.js";

const PROVIDER_OPTIONS = [
    { value: "",         label: "All providers" },
    { value: "ENTRA_ID", label: "Entra ID" },
    { value: "OKTA",     label: "Okta" },
    { value: "GOOGLE",   label: "Google" },
];

/**
 * What an in-flight sync makes untrue on this screen, for the provider filter in
 * force. A run streams rows in and retires departed ones only at the end, so a
 * mid-run screen mixes partial new rows, climbing counts, and prior-sync rows that
 * may already be gone upstream. `syncing` is the server's read-derived overlay.
 */
export function syncingNotice(integrations, provider, section) {
    const running = (integrations ?? []).filter(
        (i) => i?.status === "syncing" && (!provider || i.provider === provider),
    );
    if (!running.length) return null;
    const names = running.map((i) => i.displayName).filter(Boolean);
    const who = names.length === 1 ? names[0] : `${running.length} integrations`;
    // No completed run means nothing on screen is a prior state being refreshed.
    const firstEver = running.every(
        (i) => !i.lastFullSyncAt && !i.lastIncrementalSyncAt && !i.lastScopedSyncAt,
    );
    return firstEver
        ? `${who} is syncing for the first time. This list is filling in as it goes, so ${section} and their counts are incomplete until it finishes.`
        : `${who} is syncing now. Counts are climbing, some ${section} shown are from the previous sync, and anyone who has left the directory is still listed until the run finishes.`;
}

const PROVIDER_LABEL = {
    ENTRA_ID: "Entra ID",
    OKTA:     "Okta",
    GOOGLE:   "Google",
};

const PAGE_SIZE = 50;

// Start the next page while this many rows are still below the fold, so
// scrolling never stops at an empty tail.
const PREFETCH_ROWS = 12;
const NO_VISIBLE_RANGE = Object.freeze({ start: 0, end: 0, measured: false });

// The browser's per-host HTTP/1.1 ceiling.
const OWNER_RESOLVE_CONCURRENCY = 6;

// Background is split out of the shared chrome: a `bg-*` utility on a native
// <select> outranks the base-layer rule that keeps its dropdown popup readable
// in dark mode (see styles.css), so the select takes `controlCls` unbacked.
const controlCls =
    "min-h-[30px] rounded-[2px] border border-[color:var(--line)] px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]";
const inputCls = `${controlCls} bg-transparent`;

// ── Shared cell helpers ──────────────────────────────────────────

function TimestampCell({ value }) {
    if (!value) return <span className="text-[color:var(--muted-soft)]">—</span>;
    const d = new Date(parseServerTimestamp(value));
    if (!Number.isFinite(d.getTime())) return <span className="text-[color:var(--muted-soft)]">—</span>;
    return (
        <div className="grid gap-0">
            <strong className="text-[10px] leading-[1.12] font-medium">{timeLabelFormatter.format(d)}</strong>
            <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">{dayLabelFormatter.format(d)}</span>
        </div>
    );
}

function ProviderBadge({ provider }) {
    const label = PROVIDER_LABEL[provider] ?? provider;
    return (
        <span className="inline-flex max-w-full items-center rounded-[2px] border border-[color:var(--line)] px-[6px] py-[1px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)]">
            <span className="truncate" title={provider}>{label}</span>
        </span>
    );
}

function StatusBadge({ enabled }) {
    return (
        <span className={[
            "inline-flex items-center gap-[4px] min-w-0 text-[10px] uppercase tracking-[0.04em] font-medium",
            enabled ? "text-[color:var(--green)]" : "text-[color:var(--muted)]",
        ].join(" ")}>
            <span className={`h-[6px] w-[6px] flex-none rounded-[999px] ${enabled ? "bg-[color:var(--green)]" : "bg-[color:var(--muted-soft)]"}`} />
            <span className="truncate">{enabled ? "Active" : "Disabled"}</span>
        </span>
    );
}

// ── Column schemas per section ───────────────────────────────────

const USER_COLS = [
    { id: "displayName", label: "Name",        width: 200 },
    { id: "email",       label: "Email",       width: 220 },
    { id: "provider",    label: "Provider",    width: 100 },
    { id: "department",  label: "Department",  width: 140 },
    { id: "status",      label: "Status",      width: 90 },
    { id: "title",       label: "Title",       width: 160 },
];

const GROUP_COLS = [
    { id: "displayName", label: "Group",       width: 240 },
    { id: "provider",    label: "Provider",    width: 100 },
    { id: "memberCount", label: "Members",     width: 90 },
    { id: "syncedAt",    label: "Last synced", width: 110 },
];

const DEVICE_COLS = [
    { id: "displayName",  label: "Name",         width: 200 },
    { id: "serialNumber", label: "Serial",       width: 160 },
    { id: "provider",     label: "Provider",     width: 100 },
    { id: "os",           label: "OS",           width: 140 },
    { id: "owner",        label: "Owner",        width: 220 },
];

// ── Detail tab schemas ───────────────────────────────────────────

const USER_DETAIL_TABS   = [{ id: "overview", label: "Overview" }, { id: "groups", label: "Groups" }, { id: "devices", label: "Devices" }];
const GROUP_DETAIL_TABS  = [{ id: "overview", label: "Overview" }, { id: "users", label: "Users" }, { id: "devices", label: "Devices" }, { id: "subgroups", label: "Sub-groups" }];
const DEVICE_DETAIL_TABS = [{ id: "overview", label: "Overview" }, { id: "users", label: "Users" }, { id: "groups", label: "Groups" }];

// Say what the list is rather than how another provider differs: only Entra writes
// group-to-device edges, so for the other two both of these are entirely
// owner-derived. Naming Entra here shows one customer another product's behaviour.
const DEVICE_GROUPS_VIA_OWNERS_HINT = "These are the groups this device's owners belong to.";
const GROUP_DEVICES_VIA_OWNERS_HINT = "These are devices registered to the people in this group.";

// `member_count` is the provider-reported total (users + devices +
// sub-groups), counted before the per-type split — so it can exceed the
// captured per-tab subsets. Tooltip on the Members column + Overview field.
const TOTAL_MEMBERS_HINT = "Total members: users, devices, and subgroups";

// ── Detail panel building blocks ─────────────────────────────────

function DetailField({ label, value, mono = false, hint }) {
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`} title={hint || undefined}>{label}</span>
            <span
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] [overflow-wrap:anywhere] ${mono ? "font-mono" : ""}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}

function LinkRow({ label, meta, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex w-full items-center justify-between gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] text-left text-[11px] text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer min-w-0"
        >
            <span className="truncate">{label}</span>
            {meta ? (
                <span className="shrink-0 text-[10px] text-[color:var(--muted)] tabular-nums">{meta}</span>
            ) : null}
        </button>
    );
}

function DetailEmptyHint({ text }) {
    return (
        <div className={`${NOTE_CLASS} text-[11px] px-[2px] py-[12px]`}>{text}</div>
    );
}

// ── Section tab content (user / group / device) ──────────────────

function UserDetailContent({ selected, detail, loading, error, activeTab, navigateToGroup, navigateToDevice, onLoadMoreGroups, groupsLoading, onLoadMoreDevices, devicesLoading }) {
    if (activeTab === "overview") {
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Display name" value={selected.displayName} />
                    <DetailField label="Email" value={selected.email} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Status" value={selected.accountEnabled ? "Active" : "Disabled"} />
                    <DetailField label="Department" value={selected.department} />
                    <DetailField label="Title" value={selected.title} />
                    {selected.upn ? <DetailField label="UPN" value={selected.upn} /> : null}
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
                {detail ? (
                    <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[10px]">
                        {detail.office ? <DetailField label="Office" value={detail.office} /> : null}
                        {detail.ou ? <DetailField label="OU" value={detail.ou} /> : null}
                        {detail.sid ? <DetailField label="SID" value={detail.sid} mono /> : null}
                        <DetailField label="Synced at" value={detail.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(detail.syncedAt)) : ""} />
                    </div>
                ) : loading ? (
                    <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading details…</span>
                ) : null}
            </div>
        );
    }
    if (error) return <div className="text-[11px] text-red-400">{error}</div>;
    if (activeTab === "devices") {
        if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading devices…</span>;
        if (!detail || !detail.devices?.length) return <DetailEmptyHint text="No devices are registered to this person." />;
        return (
            <ul className="grid gap-[4px]">
                {detail.devices.map((d) => (
                    <li key={`${d.sourceProvider}:${d.sourceId}`}>
                        <LinkRow
                            label={d.displayName || d.serialNumber || d.sourceId}
                            meta={[d.serialNumber, d.operatingSystem].filter(Boolean).join(" · ")}
                            onClick={() => navigateToDevice(d)}
                        />
                    </li>
                ))}
                {detail.nextDevicePageToken ? <LoadMoreRow onClick={onLoadMoreDevices} loading={devicesLoading} /> : null}
            </ul>
        );
    }
    // Groups tab
    if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading groups…</span>;
    if (!detail || !detail.groups?.length) return <DetailEmptyHint text="Not a member of any groups." />;
    return (
        <ul className="grid gap-[4px]">
            {detail.groups.map((g) => (
                <li key={`${g.sourceProvider}:${g.sourceId}`}>
                    <LinkRow
                        label={g.displayName}
                        meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                        onClick={() => navigateToGroup(g)}
                    />
                </li>
            ))}
            {detail.nextGroupPageToken ? <LoadMoreRow onClick={onLoadMoreGroups} loading={groupsLoading} /> : null}
        </ul>
    );
}

// Loads itself when it scrolls into view. Every paginated drawer list ends with
// one, so they all page on scroll without threading a handler through each
// detail body. Falls back to a button where IntersectionObserver is missing.
function LoadMoreRow({ onClick, loading }) {
    const ref = useRef(null);
    const fire = useRef(onClick);
    fire.current = onClick;

    useEffect(() => {
        const node = ref.current;
        if (!node || loading || typeof IntersectionObserver !== "function") return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) fire.current?.();
        }, { rootMargin: "120px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [loading]);

    return (
        <li ref={ref} className="mt-[6px] text-center">
            <button
                type="button"
                onClick={onClick}
                disabled={loading}
                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer disabled:opacity-40"
            >
                {loading ? "Loading…" : "Load more"}
            </button>
        </li>
    );
}

function GroupDetailContent({ selected, detail, loading, error, activeTab, navigateToGroup, navigateToUser, navigateToDevice, onLoadMoreUsers, usersLoading, onLoadMoreDevices, devicesLoading, onLoadMoreSubGroups, subGroupsLoading }) {
    if (activeTab === "overview") {
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Group name" value={selected.displayName} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Members" value={String(selected.memberCount ?? 0)} hint={TOTAL_MEMBERS_HINT} />
                    <DetailField label="Last synced" value={selected.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(selected.syncedAt)) : ""} />
                    {selected.groupType ? <DetailField label="Type" value={selected.groupType} /> : null}
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
                {selected.description ? (
                    <div className="grid gap-[6px] border-t border-[color:var(--line)] pt-[10px]">
                        <DetailField label="Description" value={selected.description} />
                    </div>
                ) : null}
            </div>
        );
    }
    if (error) return <div className="text-[11px] text-red-400">{error}</div>;
    if (activeTab === "users") {
        if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading users…</span>;
        if (!detail || !detail.users?.length) return <DetailEmptyHint text="No direct user members." />;
        return (
            <ul className="grid gap-[4px]">
                {detail.users.map((u) => (
                    <li key={`${u.sourceProvider}:${u.sourceId}`}>
                        <LinkRow
                            label={u.displayName || u.email || u.sourceId}
                            meta={u.email}
                            onClick={() => navigateToUser(u)}
                        />
                    </li>
                ))}
                {detail.nextUserPageToken ? <LoadMoreRow onClick={onLoadMoreUsers} loading={usersLoading} /> : null}
            </ul>
        );
    }
    if (activeTab === "devices") {
        if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading devices…</span>;
        // The list is the group's own device edges unioned with devices owned by
        // its members, so "no direct device members" was wrong for everyone and
        // named a category that does not exist outside Entra.
        const viaOwners = selected.sourceProvider !== "ENTRA_ID";
        if (!detail || !detail.devices?.length) {
            return (
                <DetailEmptyHint
                    text={
                        viaOwners
                            ? "None of this group's members have a device registered."
                            : "No devices in this group, and none registered to its members."
                    }
                />
            );
        }
        return (
            <div className="grid gap-[8px]">
                {viaOwners ? <span className={`${NOTE_CLASS} text-[10px] leading-[1.5]`}>{GROUP_DEVICES_VIA_OWNERS_HINT}</span> : null}
                <ul className="grid gap-[4px]">
                    {detail.devices.map((d) => (
                        <li key={`${d.sourceProvider}:${d.sourceId}`}>
                            <LinkRow
                                label={d.displayName || d.serialNumber || d.sourceId}
                                meta={d.serialNumber || d.ownerDisplayName || (d.ownerRef?.[0] ?? "")}
                                onClick={() => navigateToDevice(d)}
                            />
                        </li>
                    ))}
                    {detail.nextDevicePageToken ? <LoadMoreRow onClick={onLoadMoreDevices} loading={devicesLoading} /> : null}
                </ul>
            </div>
        );
    }
    // sub-groups
    if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading…</span>;
    if (!detail || !detail.subGroups?.length) return <DetailEmptyHint text="No direct sub-groups." />;
    return (
        <ul className="grid gap-[4px]">
            {detail.subGroups.map((g) => (
                <li key={`${g.sourceProvider}:${g.sourceId}`}>
                    <LinkRow
                        label={g.displayName}
                        meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                        onClick={() => navigateToGroup(g)}
                    />
                </li>
            ))}
            {detail.nextSubGroupPageToken ? <LoadMoreRow onClick={onLoadMoreSubGroups} loading={subGroupsLoading} /> : null}
        </ul>
    );
}

function DeviceDetailContent({ selected, detail, loading, error, activeTab, navigateToUser, navigateToGroup, onLoadMoreGroups, groupsLoading }) {
    if (activeTab === "overview") {
        const ownerLabel = (() => {
            if (selected.ownerDisplayName) return selected.ownerDisplayName;
            if (!selected.ownerRef?.length) return "";
            const isHuman = (s) => s.includes("@") || s.startsWith("CN=");
            const human = selected.ownerRef.filter(isHuman);
            const guids = selected.ownerRef.filter((s) => !isHuman(s));
            return [...human, ...guids].join(", ");
        })();
        return (
            <div className="grid gap-[12px]">
                <div className="grid gap-[10px] [grid-template-columns:repeat(2,minmax(0,1fr))]">
                    <DetailField label="Name" value={selected.displayName} />
                    <DetailField label="Provider" value={PROVIDER_LABEL[selected.sourceProvider] ?? selected.sourceProvider} />
                    <DetailField label="Serial" value={selected.serialNumber} mono />
                    <DetailField label="Operating system" value={[selected.operatingSystem, selected.osVersion].filter(Boolean).join(" ")} />
                    <DetailField label="Owner" value={ownerLabel} />
                    <DetailField label="Last synced" value={selected.syncedAt ? fullTimestampFormatter.format(parseServerTimestamp(selected.syncedAt)) : ""} />
                    <DetailField label="Source ID" value={selected.sourceId} mono />
                </div>
            </div>
        );
    }
    if (error) return <div className="text-[11px] text-red-400">{error}</div>;
    if (activeTab === "users") {
        if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading users…</span>;
        // `owners` is the subset of `ownerRef` that resolved to a synced user, so a
        // scoped sync leaving some unresolved is ordinary rather than an error.
        if (!detail || !detail.owners?.length) {
            return (
                <DetailEmptyHint
                    text={
                        selected.ownerRef?.length
                            ? "This device has owners, but none of them are in the synced directory."
                            : "No owner is registered on this device."
                    }
                />
            );
        }
        return (
            <ul className="grid gap-[4px]">
                {detail.owners.map((u) => (
                    <li key={`${u.sourceProvider}:${u.sourceId}`}>
                        <LinkRow
                            label={u.displayName || u.email || u.sourceId}
                            meta={u.email || ""}
                            onClick={() => navigateToUser(u)}
                        />
                    </li>
                ))}
            </ul>
        );
    }
    // Groups tab
    if (loading && !detail) return <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading groups…</span>;
    const viaOwners = selected.sourceProvider !== "ENTRA_ID";
    if (!detail || !detail.groups?.length) {
        return <DetailEmptyHint text={viaOwners ? "This device's owners are not in any groups." : "This device is not in any groups."} />;
    }
    return (
        <div className="grid gap-[8px]">
            {viaOwners ? <span className={`${NOTE_CLASS} text-[10px] leading-[1.5]`}>{DEVICE_GROUPS_VIA_OWNERS_HINT}</span> : null}
            <ul className="grid gap-[4px]">
                {detail.groups.map((g) => (
                    <li key={`${g.sourceProvider}:${g.sourceId}`}>
                        <LinkRow
                            label={g.displayName}
                            meta={`${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`}
                            onClick={() => navigateToGroup(g)}
                        />
                    </li>
                ))}
                {detail.nextGroupPageToken ? <LoadMoreRow onClick={onLoadMoreGroups} loading={groupsLoading} /> : null}
            </ul>
        </div>
    );
}

// ── Component ────────────────────────────────────────────────────

export default function DirectoryBrowserTab({ section = "users", focusHeader, onSectionChange }) {
    const [provider, setProvider] = useState("");
    const [search, setSearch] = useState("");

    const [users, setUsers] = useState([]);
    const [usersToken, setUsersToken] = useState(null);
    const [usersError, setUsersError] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);

    const [groups, setGroups] = useState([]);
    const [groupsToken, setGroupsToken] = useState(null);
    const [groupsError, setGroupsError] = useState(null);
    const [groupsLoading, setGroupsLoading] = useState(false);

    const [devices, setDevices] = useState([]);
    const [devicesToken, setDevicesToken] = useState(null);
    const [devicesError, setDevicesError] = useState(null);
    const [devicesLoading, setDevicesLoading] = useState(false);

    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);

    const [userDetail, setUserDetail] = useState(null);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [userDetailError, setUserDetailError] = useState(null);
    const [groupDetail, setGroupDetail] = useState(null);
    const [groupDetailLoading, setGroupDetailLoading] = useState(false);
    const [groupDetailError, setGroupDetailError] = useState(null);
    // Refetched with the active list rather than polled: the same refetch that
    // brings finished rows in is what clears the banner.
    const [integrations, setIntegrations] = useState([]);

    const [deviceDetail, setDeviceDetail] = useState(null);
    const [deviceDetailLoading, setDeviceDetailLoading] = useState(false);
    const [deviceDetailError, setDeviceDetailError] = useState(null);
    const [groupUsersLoading, setGroupUsersLoading] = useState(false);
    const [groupDevicesLoading, setGroupDevicesLoading] = useState(false);
    const [groupSubGroupsLoading, setGroupSubGroupsLoading] = useState(false);
    const [userGroupsLoading, setUserGroupsLoading] = useState(false);
    const [userDevicesLoading, setUserDevicesLoading] = useState(false);
    const [deviceGroupsLoading, setDeviceGroupsLoading] = useState(false);

    // Drawer tab state. Reset to "overview" any time the selection
    // changes so we don't show a stale "Groups" tab against a
    // freshly-selected user that may not have its detail loaded yet.
    const [detailTab, setDetailTab] = useState("overview");
    useEffect(() => { setDetailTab("overview"); }, [selectedUser, selectedGroup, selectedDevice, section]);

    const [ownerCache, setOwnerCache] = useState({});

    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const usersRequest = useRef(0);
    const groupsRequest = useRef(0);
    const devicesRequest = useRef(0);

    const fetchUsers = useCallback(async (token) => {
        const request = ++usersRequest.current;
        const current = () => mountedRef.current && request === usersRequest.current;
        setUsersLoading(true);
        if (!token) setUsersError(null);
        try {
            const res = await listDirectoryUsers({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.users ?? [];
            setUsers((prev) => (token ? [...prev, ...list] : list));
            setUsersToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setUsersError(`Failed to load users: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setUsersLoading(false);
        }
    }, [provider, search]);

    const fetchGroups = useCallback(async (token) => {
        const request = ++groupsRequest.current;
        const current = () => mountedRef.current && request === groupsRequest.current;
        setGroupsLoading(true);
        if (!token) setGroupsError(null);
        try {
            const res = await listDirectoryGroups({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.groups ?? [];
            setGroups((prev) => (token ? [...prev, ...list] : list));
            setGroupsToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setGroupsError(`Failed to load groups: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setGroupsLoading(false);
        }
    }, [provider, search]);

    const fetchDevices = useCallback(async (token) => {
        const request = ++devicesRequest.current;
        const current = () => mountedRef.current && request === devicesRequest.current;
        setDevicesLoading(true);
        if (!token) setDevicesError(null);
        try {
            const res = await listDirectoryDevices({
                provider: provider || undefined, search: search || undefined,
                page_size: PAGE_SIZE, page_token: token,
            });
            if (!current()) return;
            const list = res?.devices ?? [];
            setDevices((prev) => (token ? [...prev, ...list] : list));
            setDevicesToken(res?.nextPageToken ?? null);
        } catch (err) {
            if (!current()) return;
            setDevicesError(`Failed to load devices: ${err.message ?? String(err)}`);
        } finally {
            if (current()) setDevicesLoading(false);
        }
    }, [provider, search]);

    useEffect(() => {
        if (section === "users")   fetchUsers();
        if (section === "groups")  fetchGroups();
        if (section === "devices") fetchDevices();
    }, [section, fetchUsers, fetchGroups, fetchDevices]);

    // owner_ref → user resolution for the Devices table.
    useEffect(() => {
        if (section !== "devices" || devices.length === 0) return;
        const toResolve = new Map();
        for (const d of devices) {
            for (const ref of d.ownerRef ?? []) {
                // Do not filter by ref shape. The query resolves an email, and
                // Google's owner_ref is always one.
                if (!ref) continue;
                const key = `${d.sourceProvider}:${ref}`;
                if (ownerCache[key]) continue;
                toResolve.set(key, { provider: d.sourceProvider, sourceId: ref });
            }
        }
        if (toResolve.size === 0) return;
        setOwnerCache((prev) => {
            const next = { ...prev };
            for (const key of toResolve.keys()) next[key] = "pending";
            return next;
        });
        // No cleanup flag: `ownerCache` is a dep, so cancelling on re-run would
        // kill the pool after its first resolution. The pending marks above are
        // what stop the re-entered effect starting a second one.
        const queue = [...toResolve.entries()];
        const resolveOne = async ([key, { provider: p, sourceId }]) => {
            try {
                const u = await getDirectoryUser(p, sourceId);
                setOwnerCache((prev) => ({ ...prev, [key]: u && u.sourceId ? u : "missing" }));
            } catch (err) {
                setOwnerCache((prev) => ({ ...prev, [key]: err?.status === 404 ? "missing" : "error" }));
            }
        };
        const drain = async () => {
            for (let entry = queue.shift(); entry; entry = queue.shift()) {
                await resolveOne(entry);
            }
        };
        // A worker shifts before its first await, so `queue.length` in the bound
        // would shrink as the pool starts.
        const workers = Math.min(OWNER_RESOLVE_CONCURRENCY, queue.length);
        for (let i = 0; i < workers; i += 1) drain();
    }, [section, devices, ownerCache]);

    // Best-effort: a failure here must not take the list down with it.
    useEffect(() => {
        let cancelled = false;
        listDirectoryIntegrations()
            .then((r) => { if (!cancelled) setIntegrations(r?.integrations ?? []); })
            .catch((err) => { console.warn("Failed to load integration status:", err); });
        return () => { cancelled = true; };
    }, [section, provider]);

    // Detail fetches.
    useEffect(() => {
        if (!selectedUser) { setUserDetail(null); setUserDetailError(null); return; }
        let cancelled = false;
        setUserDetailLoading(true);
        setUserDetailError(null);
        setUserDetail(null);
        getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
            groupPageSize: PAGE_SIZE,
            devicePageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setUserDetail(d); })
            .catch((err) => { if (!cancelled) setUserDetailError(`Failed to load user detail: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setUserDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedUser]);

    useEffect(() => {
        if (!selectedDevice) { setDeviceDetail(null); setDeviceDetailError(null); return; }
        let cancelled = false;
        setDeviceDetailLoading(true);
        setDeviceDetailError(null);
        setDeviceDetail(null);
        getDirectoryDevice({
            sourceProvider: selectedDevice.sourceProvider,
            sourceId: selectedDevice.sourceId,
            groupPageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setDeviceDetail(d); })
            .catch((err) => { if (!cancelled) setDeviceDetailError(`Failed to load device detail: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setDeviceDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedDevice]);

    useEffect(() => {
        if (!selectedGroup) { setGroupDetail(null); setGroupDetailError(null); return; }
        let cancelled = false;
        setGroupDetailLoading(true);
        setGroupDetailError(null);
        setGroupDetail(null);
        getDirectoryGroup({
            sourceProvider: selectedGroup.sourceProvider,
            sourceId: selectedGroup.sourceId,
            userPageSize: PAGE_SIZE,
            devicePageSize: PAGE_SIZE,
            subGroupPageSize: PAGE_SIZE,
        })
            .then((d) => { if (!cancelled) setGroupDetail(d); })
            .catch((err) => { if (!cancelled) setGroupDetailError(`Failed to load group detail: ${err.message ?? String(err)}`); })
            .finally(() => { if (!cancelled) setGroupDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedGroup]);

    const navigateToGroup = useCallback((g) => {
        onSectionChange?.("groups");
        setSelectedUser(null);
        setSelectedDevice(null);
        setSelectedGroup(g);
        setGroups((prev) => (prev.some((x) => x.sourceId === g.sourceId) ? prev : [g, ...prev]));
    }, [onSectionChange]);

    const navigateToUser = useCallback((u) => {
        onSectionChange?.("users");
        setSelectedGroup(null);
        setSelectedDevice(null);
        setSelectedUser(u);
        setUsers((prev) => (prev.some((x) => x.sourceId === u.sourceId) ? prev : [u, ...prev]));
    }, [onSectionChange]);

    const navigateToDevice = useCallback((d) => {
        onSectionChange?.("devices");
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedDevice(d);
        setDevices((prev) => (prev.some((x) => x.sourceId === d.sourceId) ? prev : [d, ...prev]));
    }, [onSectionChange]);

    // Drawer "Load more": fetch the next user/device page and append. The
    // group RPC returns both lists each call, so we keep only the page we
    // asked for and leave the other tab's state untouched.
    const loadMoreGroupUsers = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextUserPageToken || groupUsersLoading) return;
        setGroupUsersLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                userPageToken: groupDetail.nextUserPageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                users: [...(prev.users ?? []), ...(res?.users ?? [])],
                nextUserPageToken: res?.nextUserPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more group users:", err);
        } finally {
            if (mountedRef.current) setGroupUsersLoading(false);
        }
    }, [selectedGroup, groupDetail, groupUsersLoading]);

    const loadMoreUserGroups = useCallback(async () => {
        if (!selectedUser || !userDetail?.nextGroupPageToken || userGroupsLoading) return;
        setUserGroupsLoading(true);
        try {
            const res = await getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
                groupPageToken: userDetail.nextGroupPageToken,
            });
            if (!mountedRef.current) return;
            setUserDetail((prev) => (prev ? {
                ...prev,
                groups: [...(prev.groups ?? []), ...(res?.groups ?? [])],
                nextGroupPageToken: res?.nextGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more user groups:", err);
        } finally {
            if (mountedRef.current) setUserGroupsLoading(false);
        }
    }, [selectedUser, userDetail, userGroupsLoading]);

    const loadMoreUserDevices = useCallback(async () => {
        if (!selectedUser || !userDetail?.nextDevicePageToken || userDevicesLoading) return;
        setUserDevicesLoading(true);
        try {
            const res = await getDirectoryUser(selectedUser.sourceProvider, selectedUser.sourceId, {
                devicePageToken: userDetail.nextDevicePageToken,
            });
            if (!mountedRef.current) return;
            setUserDetail((prev) => (prev ? {
                ...prev,
                devices: [...(prev.devices ?? []), ...(res?.devices ?? [])],
                nextDevicePageToken: res?.nextDevicePageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more user devices:", err);
        } finally {
            if (mountedRef.current) setUserDevicesLoading(false);
        }
    }, [selectedUser, userDetail, userDevicesLoading]);

    const loadMoreDeviceGroups = useCallback(async () => {
        if (!selectedDevice || !deviceDetail?.nextGroupPageToken || deviceGroupsLoading) return;
        setDeviceGroupsLoading(true);
        try {
            const res = await getDirectoryDevice({
                sourceProvider: selectedDevice.sourceProvider,
                sourceId: selectedDevice.sourceId,
                groupPageToken: deviceDetail.nextGroupPageToken,
            });
            if (!mountedRef.current) return;
            setDeviceDetail((prev) => (prev ? {
                ...prev,
                groups: [...(prev.groups ?? []), ...(res?.groups ?? [])],
                nextGroupPageToken: res?.nextGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more device groups:", err);
        } finally {
            if (mountedRef.current) setDeviceGroupsLoading(false);
        }
    }, [selectedDevice, deviceDetail, deviceGroupsLoading]);

    const loadMoreGroupSubGroups = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextSubGroupPageToken || groupSubGroupsLoading) return;
        setGroupSubGroupsLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                subGroupPageToken: groupDetail.nextSubGroupPageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                subGroups: [...(prev.subGroups ?? []), ...(res?.subGroups ?? [])],
                nextSubGroupPageToken: res?.nextSubGroupPageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more sub-groups:", err);
        } finally {
            if (mountedRef.current) setGroupSubGroupsLoading(false);
        }
    }, [selectedGroup, groupDetail, groupSubGroupsLoading]);

    const loadMoreGroupDevices = useCallback(async () => {
        if (!selectedGroup || !groupDetail?.nextDevicePageToken || groupDevicesLoading) return;
        setGroupDevicesLoading(true);
        try {
            const res = await getDirectoryGroup({
                sourceProvider: selectedGroup.sourceProvider,
                sourceId: selectedGroup.sourceId,
                devicePageToken: groupDetail.nextDevicePageToken,
            });
            if (!mountedRef.current) return;
            setGroupDetail((prev) => (prev ? {
                ...prev,
                devices: [...(prev.devices ?? []), ...(res?.devices ?? [])],
                nextDevicePageToken: res?.nextDevicePageToken ?? "",
            } : prev));
        } catch (err) {
            console.warn("Failed to load more group devices:", err);
        } finally {
            if (mountedRef.current) setGroupDevicesLoading(false);
        }
    }, [selectedGroup, groupDetail, groupDevicesLoading]);

    // ── Cell renderers per section ───────────────────────────────

    const userRenderCell = useCallback((u, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={u.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "email": return (
                <OverflowTooltip as="span" text={u.email || ""}
                    className="text-[11px] leading-[1.18] text-[color:var(--muted)]" />
            );
            case "provider":   return <ProviderBadge provider={u.sourceProvider} />;
            case "department": return <OverflowTooltip as="span" text={u.department || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            case "status":     return <StatusBadge enabled={u.accountEnabled} />;
            case "title":      return <OverflowTooltip as="span" text={u.title || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            default: return null;
        }
    }, []);

    const groupRenderCell = useCallback((g, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={g.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "provider":    return <ProviderBadge provider={g.sourceProvider} />;
            case "memberCount": return <span className="text-[11px] text-[color:var(--ink)] tabular-nums" title={TOTAL_MEMBERS_HINT}>{g.memberCount}</span>;
            case "syncedAt":    return <TimestampCell value={g.syncedAt} />;
            default: return null;
        }
    }, []);

    const deviceRenderCell = useCallback((d, col) => {
        switch (col.id) {
            case "displayName": return (
                <OverflowTooltip as="strong" text={d.displayName || "—"}
                    className="text-[12px] leading-[1.08] font-medium text-[color:var(--ink)]" />
            );
            case "serialNumber": return (
                <OverflowTooltip as="span" text={d.serialNumber || "—"}
                    className="text-[11px] leading-[1.18] font-mono text-[color:var(--muted)]" />
            );
            case "provider": return <ProviderBadge provider={d.sourceProvider} />;
            case "os": {
                const label = [d.operatingSystem, d.osVersion].filter(Boolean).join(" ");
                return <OverflowTooltip as="span" text={label || "—"} className="text-[11px] text-[color:var(--muted)]" />;
            }
            case "owner": {
                const looksHumanReadable = (s) => s.includes("@") || s.startsWith("CN=");
                const refs = d.ownerRef ?? [];
                let resolvedUser = null;
                for (const ref of refs) {
                    const entry = ownerCache[`${d.sourceProvider}:${ref}`];
                    if (entry && entry !== "pending" && entry !== "missing" && entry !== "error") {
                        resolvedUser = entry;
                        break;
                    }
                }
                // Resolved user before readable ref: reversed, a Google row
                // shows an email where an Okta row beside it shows a name.
                const label =
                    d.ownerDisplayName
                    || (resolvedUser?.displayName || resolvedUser?.email || resolvedUser?.upn || "")
                    || refs.find(looksHumanReadable)
                    || refs[0]
                    || "";
                if (!label) return <span className="text-[color:var(--muted-soft)]">—</span>;
                if (resolvedUser) {
                    return (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigateToUser(resolvedUser); }}
                            className="text-left text-[11px] text-[color:var(--ink)] hover:text-[color:var(--green)] cursor-pointer truncate"
                            title={label}
                        >
                            {label}
                        </button>
                    );
                }
                return <OverflowTooltip as="span" text={label} className="text-[11px] text-[color:var(--muted)]" />;
            }
            default: return null;
        }
    }, [ownerCache, navigateToUser]);

    // ── Active section data ──────────────────────────────────────

    const activeCols     = section === "users" ? USER_COLS : section === "groups" ? GROUP_COLS : DEVICE_COLS;
    const activeRows     = section === "users" ? users : section === "groups" ? groups : devices;
    const activeError    = section === "users" ? usersError : section === "groups" ? groupsError : devicesError;
    const activeLoading  = section === "users" ? usersLoading : section === "groups" ? groupsLoading : devicesLoading;
    const activeToken    = section === "users" ? usersToken : section === "groups" ? groupsToken : devicesToken;
    const activeFetch    = section === "users" ? fetchUsers : section === "groups" ? fetchGroups : fetchDevices;

    // Scroll pagination. DataTable reports the row band the reader can actually
    // see; once that band reaches the tail, pull the next page. The token is
    // recorded before the fetch so a band that advances again mid-flight cannot
    // request the same page twice.
    const [visibleRange, setVisibleRange] = useState(NO_VISIBLE_RANGE);
    const requestedTokenRef = useRef(null);
    useEffect(() => { requestedTokenRef.current = null; }, [section, provider, search]);
    useEffect(() => {
        if (!visibleRange.measured || !activeToken || activeLoading) return;
        if (requestedTokenRef.current === activeToken) return;
        if (visibleRange.end < activeRows.length - PREFETCH_ROWS) return;
        requestedTokenRef.current = activeToken;
        activeFetch(activeToken);
    }, [visibleRange, activeToken, activeLoading, activeRows.length, activeFetch]);
    const activeRender   = section === "users" ? userRenderCell : section === "groups" ? groupRenderCell : deviceRenderCell;

    const cols = useMemo(() => {
        const effective = activeCols.map((c) => ({ ...c, width: c.width ?? 140 }));
        const tracks = effective.map((c) => `${c.width}px`);
        const gridCols = `${tracks.join(" ")} minmax(0,1fr)`;
        return { effective, gridCols, widths: { setWidth: () => {} } };
    }, [activeCols]);

    const onRowClick = useCallback((row) => {
        if (section === "users") {
            setSelectedUser((cur) => cur?.sourceId === row.sourceId ? null : row);
        } else if (section === "groups") {
            setSelectedGroup((cur) => cur?.sourceId === row.sourceId ? null : row);
        } else {
            setSelectedDevice((cur) => cur?.sourceId === row.sourceId ? null : row);
        }
    }, [section]);

    // ── Drawer state ─────────────────────────────────────────────

    const hasSelection =
        (section === "users"   && !!selectedUser) ||
        (section === "groups"  && !!selectedGroup) ||
        (section === "devices" && !!selectedDevice);

    const closeSelection = useCallback(() => {
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedDevice(null);
    }, []);

    // Clear only the selections that don't belong to the now-active section.
    // A manual tab switch still drops a stale selection (so it can't resurface
    // when the operator returns to that tab), but a programmatic cross-section
    // navigate (e.g. clicking a group in a user's Groups list) keeps the
    // selection it just set — clearing all three here would race that setter
    // and leave the drawer closed.
    useEffect(() => {
        if (section !== "users") setSelectedUser(null);
        if (section !== "groups") setSelectedGroup(null);
        if (section !== "devices") setSelectedDevice(null);
    }, [section]);

    // ── Headers ──────────────────────────────────────────────────

    // Right header — entity name + close (×). The close button on the
    // tab clears the selection, which unmounts the drawer entirely.
    // There's no separate "collapse" affordance: the drawer is bound
    // to the selection, not toggleable on its own.
    const detailTitle =
        section === "users"   && selectedUser   ? (selectedUser.displayName || selectedUser.email || selectedUser.sourceId) :
        section === "groups"  && selectedGroup  ? selectedGroup.displayName :
        section === "devices" && selectedDevice ? (selectedDevice.displayName || selectedDevice.sourceId) :
        "";

    // pt-0 here: SplitPage's right column header container already
    // applies py-[11px] via PANE_TOPBAR_CLASS. Without this override
    // we'd double-pad and the right tab sits one row lower than the
    // left focus tabs. The close chevron lives in the actions slot
    // SplitPage injects; it's routed through `onCloseDetail` to clear
    // the selection (which collapses the drawer via `forceCollapse`).
    const detailTabStrip = (
        <TabStrip
            tabs={[{
                id: "detail",
                label: detailTitle,
                active: true,
                dot: "bg-[color:var(--green)]",
            }]}
            className="pt-0"
        />
    );

    // ── Right content (detail body) ──────────────────────────────

    let detailTabs = null;
    let detailBody = null;
    if (section === "users" && selectedUser) {
        detailTabs = USER_DETAIL_TABS;
        detailBody = (
            <UserDetailContent
                selected={selectedUser}
                detail={userDetail}
                loading={userDetailLoading}
                error={userDetailError}
                activeTab={detailTab}
                navigateToGroup={navigateToGroup}
                navigateToDevice={navigateToDevice}
                onLoadMoreGroups={loadMoreUserGroups}
                groupsLoading={userGroupsLoading}
                onLoadMoreDevices={loadMoreUserDevices}
                devicesLoading={userDevicesLoading}
            />
        );
    } else if (section === "groups" && selectedGroup) {
        detailTabs = GROUP_DETAIL_TABS;
        detailBody = (
            <GroupDetailContent
                selected={selectedGroup}
                detail={groupDetail}
                loading={groupDetailLoading}
                error={groupDetailError}
                activeTab={detailTab}
                navigateToGroup={navigateToGroup}
                navigateToUser={navigateToUser}
                navigateToDevice={navigateToDevice}
                onLoadMoreUsers={loadMoreGroupUsers}
                usersLoading={groupUsersLoading}
                onLoadMoreDevices={loadMoreGroupDevices}
                devicesLoading={groupDevicesLoading}
                onLoadMoreSubGroups={loadMoreGroupSubGroups}
                subGroupsLoading={groupSubGroupsLoading}
            />
        );
    } else if (section === "devices" && selectedDevice) {
        detailTabs = DEVICE_DETAIL_TABS;
        detailBody = (
            <DeviceDetailContent
                selected={selectedDevice}
                detail={deviceDetail}
                loading={deviceDetailLoading}
                error={deviceDetailError}
                activeTab={detailTab}
                navigateToUser={navigateToUser}
                navigateToGroup={navigateToGroup}
                onLoadMoreGroups={loadMoreDeviceGroups}
                groupsLoading={deviceGroupsLoading}
            />
        );
    }

    const syncNotice = syncingNotice(integrations, provider, section);

    const leftContent = (
        <div className="flex h-full min-h-0 min-w-0 flex-col py-[8px] gap-[10px]">
            <div className="shrink-0 grid gap-[8px]">
                {syncNotice ? (
                    <div
                        role="status"
                        className={`${NOTE_CLASS} text-[11px] leading-[1.5] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[6px]`}
                    >
                        {syncNotice}
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center justify-between gap-[8px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] text-[color:var(--muted)]">
                    <span>
                        <strong className="text-[color:var(--ink)]">{formatCompact(activeRows.length)}</strong> shown
                    </span>
                    <div className="flex items-center gap-[8px] min-w-0">
                        {activeLoading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                        {activeError ? (
                            <span className="text-red-400 truncate max-w-[280px]" title={activeError}>{activeError}</span>
                        ) : null}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-[8px]">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={`Search ${section}…`}
                        aria-label={`Search ${section}`}
                        className={`${inputCls} flex-1 min-w-[200px]`}
                    />
                    <select
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        aria-label="Filter by provider"
                        className={`${controlCls} min-w-[140px]`}
                    >
                        {PROVIDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="flex flex-1 min-h-0 min-w-0 flex-col">
                <DataTable
                    cols={cols}
                    rows={activeRows}
                    getRowId={(r) => `${r.sourceProvider}:${r.sourceId}`}
                    renderCell={activeRender}
                    onRowClick={onRowClick}
                    loading={activeLoading}
                    skeletonRows={6}
                    onVisibleRange={setVisibleRange}
                    empty={
                        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px] my-[12px]">
                            <strong className="text-[13px] leading-[1.08] font-medium">
                                {syncNotice ? `No ${section} yet` : `No ${section} found`}
                            </strong>
                            <span className={NOTE_CLASS}>
                                {/* Never tell someone to start a sync that is running. */}
                                {syncNotice
                                    ? "A sync is running now. Rows appear here as it goes."
                                    : search || provider
                                      ? "Try clearing the search or provider filter."
                                      : "Sync a directory integration to populate this view."}
                            </span>
                        </div>
                    }
                />
                {activeToken && activeRows.length ? (
                    <div className={`${NOTE_CLASS} shrink-0 border-t border-[color:var(--line-soft)] py-[6px] text-center text-[10px]`}>
                        {activeLoading ? "Loading more…" : "Scroll for more"}
                    </div>
                ) : null}
            </div>
        </div>
    );

    const rightContent = () => (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            {detailTabs && detailTabs.length > 1 ? (
                <div className="shrink-0 flex items-center gap-[4px] border-b border-[color:var(--line)] py-[8px]">
                    {detailTabs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setDetailTab(t.id)}
                            aria-pressed={detailTab === t.id}
                            className={[
                                "h-[26px] rounded-[2px] border px-[10px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase cursor-pointer transition-colors",
                                detailTab === t.id
                                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                                    : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                            ].join(" ")}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            ) : null}
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                {detailBody}
            </div>
        </div>
    );

    return (
        // AdminPage wraps the view in a px-[var(--main-inset)] section,
        // and SplitPage's ContentShell adds the same inset per column —
        // the outer one would double up at the screen edges. Negative
        // margin lets the column-level inset run alone.
        <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
            <SplitPage
                defaultDetailRatio={0.42}
                focusHeader={focusHeader}
                leftContent={leftContent}
                rightHeader={detailTabStrip}
                rightContent={rightContent}
                // Drawer is bound to the selection: closed when nothing
                // selected, open whenever something is. The chevron on
                // the right header routes through `onCloseDetail` to
                // clear the selection — that flips forceCollapse to true
                // which collapses the panel via the controlled effect
                // inside SplitPage.
                forceCollapse={!hasSelection}
                onCloseDetail={closeSelection}
            />
        </div>
    );
}
